/**
 * Compensation / rate-card computation for generated documents.
 *
 * Turns a set of line items + pricing mode + tax info into a structured
 * compensation result the document renderer can lay out. Adapts to every
 * billing scenario: monthly recurring, hourly (+ crew), flat project,
 * per-unit / per-visit, tiered, and rate-card (price list) presentation.
 */
import type { CrewRate, PricingMode } from '../types/agreement'

export interface CompensationLineItem {
  description?: string
  quantity?: number
  is_variable?: boolean
  unit_type?: string
  unit_price?: number
  total_price?: number
}

export interface CompensationInput {
  lineItems: CompensationLineItem[]
  pricingMode?: PricingMode
  contractType?: string // 'one_time' | 'recurring' | ''
  salesTaxStatus?: string // 'taxable' | 'non-tax' | 'exempt' | ''
  taxRate?: number
  monthlyAmount?: number
  totalAmount?: number
  crew?: CrewRate | null
}

export interface CompensationRow {
  label: string
  amount: string // formatted currency or "Variable"
  unit: string // human unit label, e.g. "per apartment"
}

export interface CompensationResult {
  mode: PricingMode
  rows: CompensationRow[]
  /** One-line summary statements (e.g. "Monthly rate – $500.00 + Sales Tax"). */
  statements: string[]
  /** Extra notes (tax exemption, crew rate, PTO/holiday handled elsewhere). */
  notes: string[]
  showRows: boolean
  total: number
  totalFormatted: string
  showTotal: boolean
}

export function formatCurrency(value: number | null | undefined): string {
  const n = Number(value || 0)
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

/** Human-readable unit label for a unit_type slug or free-text label. */
export function formatUnitLabel(unitType: string | null | undefined): string {
  const u = (unitType || '').trim()
  if (!u) return ''
  switch (u) {
    case 'per_month':
      return 'per month'
    case 'per_visit':
      return 'per visit'
    case 'per_hour':
      return 'per hour'
    case 'per_sqft':
      return 'per sq ft'
    case 'per_unit':
      return 'per unit'
    case 'per_apartment':
      return 'per apartment'
    case 'per_bedroom':
      return 'per bedroom'
    case 'per_bathroom':
      return 'per bathroom'
    case 'per_crew_hour':
      return 'per crew-hour'
    case 'flat_rate':
    case 'flat':
      return 'flat'
    default:
      // already a friendly label, or snake_case fallback
      return u.replace(/_/g, ' ')
  }
}

function lineTotal(li: CompensationLineItem): number {
  if (li.is_variable) return 0
  if (typeof li.total_price === 'number' && li.total_price > 0) return li.total_price
  const qty = Number(li.quantity || 0)
  const price = Number(li.unit_price || 0)
  return qty * price
}

function taxSuffix(status?: string): string {
  switch ((status || '').toLowerCase()) {
    case 'taxable':
      return '+ Sales Tax'
    case 'exempt':
      return '(Tax Exempt)'
    case 'non-tax':
    case 'non_tax':
      return ''
    default:
      return ''
  }
}

export function buildCompensation(input: CompensationInput): CompensationResult {
  const {
    lineItems = [],
    pricingMode = 'total',
    contractType = '',
    salesTaxStatus = '',
    monthlyAmount = 0,
    crew = null,
  } = input

  const suffix = taxSuffix(salesTaxStatus)
  const notes: string[] = []

  // Rate-card mode: present every line as a price-list row, no single total.
  if (pricingMode === 'rate_card') {
    const rows: CompensationRow[] = lineItems
      .filter((li) => (li.description || '').trim() || li.unit_price)
      .map((li) => ({
        label: (li.description || 'Item').trim(),
        amount: li.is_variable ? 'Variable' : formatCurrency(li.unit_price),
        unit: formatUnitLabel(li.unit_type),
      }))
    if (crew && crew.rate > 0) {
      notes.push(`Crew rate: ${formatCurrency(crew.rate)} per hour for a crew of ${crew.size}.`)
    }
    return {
      mode: 'rate_card',
      rows,
      statements: [],
      notes,
      showRows: rows.length > 0,
      total: 0,
      totalFormatted: formatCurrency(0),
      showTotal: false,
    }
  }

  // Statement / total mode.
  const isRecurring = contractType === 'recurring'
  const total = lineItems.reduce((sum, li) => sum + lineTotal(li), 0)
  const statements: string[] = []

  const hasMonthly = lineItems.some((li) => (li.unit_type || '') === 'per_month')
  const hourlyItems = lineItems.filter((li) => (li.unit_type || '') === 'per_hour')

  if (isRecurring && (hasMonthly || monthlyAmount > 0)) {
    const monthly = monthlyAmount > 0 ? monthlyAmount : total
    statements.push(`Monthly rate – ${formatCurrency(monthly)}${suffix ? ' ' + suffix : ''}`)
  } else if (hourlyItems.length > 0) {
    const rate = hourlyItems[0].unit_price || 0
    let line = `${formatCurrency(rate)} per hour, per person`
    if (crew && crew.rate > 0) {
      line += ` (or ${formatCurrency(crew.rate)} per hour for a crew of ${crew.size})`
    }
    statements.push(line)
  } else if (total > 0) {
    statements.push(`Project total – ${formatCurrency(total)}${suffix ? ' ' + suffix : ''}`)
  }

  // Always also expose the line items as rows so the document can show a
  // detailed breakdown beneath the headline statement.
  const rows: CompensationRow[] = lineItems
    .filter((li) => (li.description || '').trim() || li.unit_price)
    .map((li) => ({
      label: (li.description || 'Item').trim(),
      amount: li.is_variable ? 'Variable' : formatCurrency(lineTotal(li) || li.unit_price),
      unit: formatUnitLabel(li.unit_type),
    }))

  const showTotal = !hourlyItems.length && total > 0 && rows.length > 1

  return {
    mode: 'total',
    rows,
    statements,
    notes,
    showRows: rows.length > 0,
    total,
    totalFormatted: `${formatCurrency(total)}${suffix ? ' ' + suffix : ''}`,
    showTotal,
  }
}

/**
 * Starter rate-card / line-item rows seeded when a Service Type is chosen.
 * Lets users edit/extend rather than build dynamic pricing from scratch.
 */
export interface SeedRow {
  description: string
  unit_type: string
  unit_price: number
}

export function getRateCardSeed(serviceType: string | null | undefined): SeedRow[] {
  switch ((serviceType || '').trim()) {
    case 'apartment_turnovers':
      return [
        { description: 'Studio', unit_type: 'per_apartment', unit_price: 0 },
        { description: '1 Bedroom / 1 Bath', unit_type: 'per_apartment', unit_price: 0 },
        { description: '2 Bedroom / 1 Bath', unit_type: 'per_apartment', unit_price: 0 },
        { description: '3 Bedroom / 2 Bath', unit_type: 'per_apartment', unit_price: 0 },
        { description: 'Additional Bedroom', unit_type: 'per_bedroom', unit_price: 0 },
        { description: 'Additional Bathroom', unit_type: 'per_bathroom', unit_price: 0 },
        { description: 'Mezzanine Duplex', unit_type: 'flat_rate', unit_price: 0 },
        { description: 'Basement Duplex', unit_type: 'flat_rate', unit_price: 0 },
      ]
    case 'hourly_clean':
      return [{ description: 'Cleaning labor', unit_type: 'per_hour', unit_price: 0 }]
    case 'office_cleaning':
      return [{ description: 'Office cleaning', unit_type: 'per_visit', unit_price: 0 }]
    default:
      return []
  }
}
